import { corpusSources, corpusSummary, sourceStatuses } from "../data/corpusRegistry.js";
import { operatorRegistry } from "../data/operatorRegistry.js";
import { custodySummary } from "../knowledge/corpusCustodyRegistry.js";
import { receiptLedgerSummary } from "../knowledge/receiptLedger.js";
import { useRuntimeProofStatus } from "../knowledge/runtimeProofStatus.js";
import { QuoteLedger } from "./QuoteLedger.jsx";
import { LucideIcon } from "./lucideIconMap.js";

export function EvidenceRail({ receipt, selectedOption, localLedgerEntries = [], localLedgerSummary }) {
  const summary = corpusSummary();
  const custody = custodySummary();
  const { durable, deployment } = useRuntimeProofStatus();
  const ledger = receiptLedgerSummary();
  const decisionLedger = localLedgerSummary ?? {
    total: localLedgerEntries.length,
    productionClaims: 0,
    formalVerificationClaims: 0,
    needsEvidence: 0,
    blocked: 0
  };
  const failedGates = selectedOption.gate.failed;

  return (
    <aside className="inspector proof-tower">
      <section className="proof-tower-head">
        <span>Proof Tower</span>
        <h2>Evidence & Provenance</h2>
        <p>Every visible claim remains tied to local source, operator, receipt, or deployment proof state.</p>
      </section>

      <section className="inspector-module gate-module">
        <div className="module-title">
          <LucideIcon name="ShieldCheck" />
          <h2>Evidence Gates</h2>
        </div>
        <div className="gate-list">
          {failedGates.length === 0 ? (
            <div className="gate-row pass">
              <LucideIcon name="Check" size={14} />
              <span>Required gates passed</span>
            </div>
          ) : (
            failedGates.map((gate) => (
              <div className="gate-row fail" key={gate.label}>
                <LucideIcon name="CircleSlash" size={14} />
                <span>{gate.label}</span>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="inspector-module source-module">
        <div className="module-title">
          <LucideIcon name="Library" />
          <h2>Source Custody</h2>
        </div>
        <dl className="status-grid">
          <div>
            <dt>Sources</dt>
            <dd>{summary.total}</dd>
          </div>
          <div>
            <dt>Countable</dt>
            <dd>{summary.countable}</dd>
          </div>
          <div>
            <dt>Anchors</dt>
            <dd>{summary[sourceStatuses.SOURCE_ANCHOR_ONLY] ?? 0}</dd>
          </div>
          <div>
            <dt>Licensed</dt>
            <dd>{summary[sourceStatuses.LICENSED_MODERN_TRANSLATION] ?? 0}</dd>
          </div>
        </dl>
        <div className="source-list">
          {corpusSources.slice(0, 4).map((source) => (
            <div className="source-row" key={source.id}>
              <span>{source.title}</span>
              <strong>{source.countable ? "Countable" : source.status}</strong>
            </div>
          ))}
        </div>
      </section>

      <QuoteLedger compact />

      <section className="inspector-module model-module">
        <div className="module-title">
          <LucideIcon name="Sigma" />
          <h2>Operator Models</h2>
        </div>
        {operatorRegistry.slice(0, 3).map((operator) => (
          <div className="operator-row" key={operator.id}>
            <strong>{operator.label}</strong>
            <span>{operator.statuses.join(" / ")}</span>
          </div>
        ))}
      </section>

      <section className="inspector-module receipt proof-blockers">
        <h2>Receipt & Release State</h2>
        <p>{receipt.claimBoundary}</p>
        <dl className="status-grid receipt-grid">
          <div>
            <dt>Custody</dt>
            <dd>{custody.total}</dd>
          </div>
          <div>
            <dt>Ledger</dt>
            <dd>{ledger.total}</dd>
          </div>
          <div>
            <dt>Local</dt>
            <dd>{decisionLedger.total}</dd>
          </div>
          <div>
            <dt>Server</dt>
            <dd>{durable.serverCustodyClaim ? 1 : 0}</dd>
          </div>
        </dl>
        <div className="source-list compact-source-list">
          <div className={`source-row ${deployment.liveUrlVerified ? "pass" : "fail"}`}>
            <span>Live preview</span>
            <strong>{deployment.liveUrlVerified ? "Verified" : "Blocked"}</strong>
          </div>
          <div className={`source-row ${durable.serverCustodyClaim ? "pass" : "fail"}`}>
            <span>D1/R2 custody</span>
            <strong>{durable.serverCustodyClaim ? "Smoke" : "Blocked"}</strong>
          </div>
        </div>
      </section>
    </aside>
  );
}
